/*
 * File: useOpsBoardV2Data.ts
 * Path: src/features/ops-board-v2/useOpsBoardV2Data.ts
 * Description: Public Ops Board V2 API contract and proven 30-second polling data layer.
 * Author: Larry McLean + ChatGPT
 * Created: 2026-07-10
 * Version: 1.0.0
 *
 * Change Log:
 * - 2026-07-10: v1.0.0 - Add public operator-slug fetch and 30-second refresh for the frozen Ops Board V2 shell.
 */

import React from 'react';
import { apiFetch } from '../../utils/api';

export type OpsBoardSession = {
  id: string;
  type: 'dive' | 'course';
  title: string;
  session_status: string;
  ops_status: string;
  capacity_total: number | null;
  capacity_consumed: number;
  capacity_remaining: number;
  starts_at: string;
  start_time: string;
  meet_time?: string | null;
  meet_location?: string | null;
  site_name: string;
  session_type: string;
  session_date: string;
  duration_days?: number | null;
  metadata?: {
    vessel_name?: string | null;
    lead_guide_name?: string | null;
    instructor_name?: string | null;
    location?: string | null;
    meet_time?: string | null;
    meet_location?: string | null;
    course_start_date?: string | null;
    course_end_date?: string | null;
  };
};

export type OpsBoardSummary = {
  booking_count: number;
  confirmed_pax: number;
  active_hold_pax: number;
  waitlist_pax: number;
  manual_review_count: number;
  manual_review_pax: number;
  has_pickups: boolean;
  pickup_count: number;
  has_notes: boolean;
  nitrox_requested_count: number;
  nitrox_cert_verified_count: number;
  rental_gear_count: number;
  own_gear_count: number;
  has_minors: boolean;
  roster?: Array<string | { name: string; nitrox_requested?: boolean }>;
};

export type OpsBoardApiResponse = {
  ok: boolean;
  generated_at: string;
  sunset_time?: string | null;
  weather?: {
    air_temp_c?: number | null;
    wind_kmh?: number | null;
    weather_code?: number | null;
    condition?: string | null;
  };
  operator: {
    slug: string;
    name: string;
    timezone: string;
    currency: string;
    operator_default_capacity: number;
    website?: string | null;
    logo_url?: string | null;
    location_city?: string | null;
    location_region?: string | null;
    ops_board_grace_minutes?: number | null;
  };
  window: {
    type: string;
    start: string;
    end: string;
  };
  sessions: OpsBoardSession[];
  session_summaries: Record<string, OpsBoardSummary>;
  aggregates: {
    total_sessions: number;
    total_pax: number;
    confirmed_pax_total: number;
    active_hold_pax_total: number;
    waitlist_pax_total: number;
    nitrox_total: number;
    rental_gear_total: number;
    manual_review_total: number;
    pickup_total: number;
    minor_session_count: number;
  };
};

export function useOpsBoardV2Data(
  operatorSlug?: string,
  windowParam: string = 'today_tomorrow'
) {
  const [data, setData] = React.useState<OpsBoardApiResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let isMounted = true;

    async function loadOpsBoardApi() {
      try {
        if (!operatorSlug) {
          throw new Error('operatorSlug is required for the public Ops Board V2 route');
        }

        const response = await apiFetch(
          `/api/v1/public/ops-board/${operatorSlug}?window=${windowParam}`,
          { method: 'GET' }
        );

        const json = (await response.json()) as OpsBoardApiResponse;

        console.log('AQX OPS BOARD V2 API STATUS:', response.status);
        console.log(
          'AQX OPS BOARD V2 API BODY:',
          JSON.stringify(json, null, 2)
        );

        if (!response.ok || !json.ok) {
          throw new Error(
            `Ops Board V2 API failed with HTTP ${response.status}`
          );
        }

        if (isMounted) {
          setData(json);
          setError(null);
        }
      } catch (loadError) {
        console.error('AQX OPS BOARD V2 API ERROR:', loadError);

        if (isMounted) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : 'Unknown Ops Board V2 API error'
          );
        }
      }
    }

    loadOpsBoardApi();

    const refreshTimer = window.setInterval(() => {
      loadOpsBoardApi();
    }, 30000);

    return () => {
      isMounted = false;
      window.clearInterval(refreshTimer);
    };
  }, [operatorSlug, windowParam]);

  return { data, error };
}
